import React from "react";

// Android-style favorites context menu: a card with the item's artwork on the
// left and type-aware actions on the right. The first action renders as the
// highlighted (white pill) primary; actions whose handler is omitted are
// hidden (e.g. the grid page passes no Move / Move-to-top).
const FavoritesContextMenu = ({
  item,
  type, // "tv" | "series" | "movie"
  actions: customActions, // optional override list (e.g. the home page)
  onClose,
  onPlay,
  onMove,
  onMoveToTop,
  onRemove,
}) => {
  if (!item) return null;

  const isTV = type === "tv";
  const subtitle = isTV ? "Live TV" : type === "series" ? "Series" : "Movie";
  const title = item.name || item.title || "Untitled";

  const logo =
    item.logoPath || item.logo || item.stream_icon || item.icon || item.image;
  const poster = item.poster || item.posterPath || item.thumbnail;
  const artwork = isTV ? logo : poster;

  const run = (fn) => () => {
    onClose?.();
    fn?.();
  };

  const actions = (
    isTV
      ? [
          onPlay && {
            key: "play",
            icon: "fa-play",
            label: "Play Now",
            onClick: run(onPlay),
          },
          onRemove && {
            key: "remove",
            icon: "fa-bookmark",
            label: "Clear from Favorites",
            onClick: run(onRemove),
          },
          onMoveToTop && {
            key: "top",
            icon: "fa-arrow-up",
            label: "Move to top",
            onClick: run(onMoveToTop),
          },
        ]
      : [
          onMove && {
            key: "move",
            icon: "fa-up-down",
            label: "Move",
            onClick: run(onMove),
          },
          onPlay && {
            key: "details",
            icon: "fa-circle-info",
            label: "View Details",
            onClick: run(onPlay),
          },
          onMoveToTop && {
            key: "top",
            icon: "fa-arrow-up",
            label: "Move to top",
            onClick: run(onMoveToTop),
          },
          onRemove && {
            key: "remove",
            icon: "fa-trash",
            label: "Remove from Favorites",
            danger: true,
            onClick: run(onRemove),
          },
        ]
  ).filter(Boolean);

  const list = customActions
    ? customActions.map((a) => ({ ...a, onClick: run(a.onClick) }))
    : actions;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4"
      role="presentation"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative flex w-auto max-w-xl overflow-hidden rounded-3xl border border-white/15 bg-gray-900/90 shadow-2xl backdrop-blur-2xl"
        role="presentation"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Artwork */}
        <div
          className={`relative flex shrink-0 items-center justify-center self-stretch overflow-hidden ${
            isTV
              ? "w-28 bg-gradient-to-br from-gray-800 to-gray-900 p-4"
              : "w-52 bg-black"
          }`}
        >
          {artwork ? (
            <img
              alt={title}
              className={
                isTV
                  ? "max-h-28 max-w-full object-contain"
                  : "aspect-[2/3] h-auto max-h-full w-full object-contain"
              }
              src={artwork}
            />
          ) : (
            <i
              className={`fas ${
                isTV ? "fa-image" : "fa-film"
              } text-4xl text-white/30`}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-5">
          <h3 className="text-xl font-bold leading-tight text-white">
            {title}
          </h3>
          <p className="mt-1 text-sm text-gray-400">{subtitle}</p>

          <div className="my-3 border-t border-white/10" />

          <div className="flex flex-col">
            {list.map((a, i) =>
              i === 0 ? (
                <button
                  className="mb-1 flex items-center gap-3 rounded-full bg-white px-5 py-3 font-semibold text-gray-900 transition-colors hover:bg-gray-100"
                  key={a.key}
                  type="button"
                  onClick={a.onClick}
                >
                  <i className={`fas ${a.icon} w-5 text-center`} />
                  {a.label}
                </button>
              ) : (
                <button
                  className={`flex items-center gap-3 border-t border-white/10 px-3 py-3 text-left transition-colors hover:bg-white/5 ${
                    a.danger ? "text-red-400" : "text-white"
                  }`}
                  key={a.key}
                  type="button"
                  onClick={a.onClick}
                >
                  <i
                    className={`fas ${a.icon} w-5 text-center ${
                      a.danger ? "text-red-400" : "text-gray-300"
                    }`}
                  />
                  {a.label}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavoritesContextMenu;
